import { useEffect, useRef, useState, useCallback } from 'react';
import {
  IDebugTimeMachineConfig,
  IDebugTimeMachineHookReturn,
  MessageType,
  IStateChangeData,
  IErrorData,
  IConnectionData,
  TConnectionType,
} from './types';

const DEFAULT_CONFIG: Required<IDebugTimeMachineConfig> = {
  websocketUrl: 'ws://localhost:4000/ws',
  debugMode: true, // 🔥 기본값을 true로 변경
  captureUserActions: true,
  captureErrors: true,
  captureStateChanges: true,
  maxReconnectAttempts: 5,
  reconnectInterval: 5000,
  developmentOnly: false,
  autoConnect: true,
  enableTimeTravelEngine: true,
  enableMemoryManagement: true,
  enableDOMSnapshots: false,
  enableMetrics: true,
  timeTravelConfig: {},
  memoryConfig: {},
  domSnapshotConfig: {},
};

const CONSOLE_STYLES = {
  info: 'color: #2196F3; font-weight: bold;',
  success: 'color: #4CAF50; font-weight: bold;',
  warning: 'color: #FF9800; font-weight: bold;',
  error: 'color: #F44336; font-weight: bold;',
} as const;

type TLogType = keyof typeof CONSOLE_STYLES;

declare global {
  interface XMLHttpRequest {
    _debugTM?: {
      requestId: string;
      method: string;
      url: string;
      startTime: number;
      shouldCapture: boolean;
    };
  }
}

export function useDebugTimeMachine(
  userConfig: IDebugTimeMachineConfig = {}
): IDebugTimeMachineHookReturn {
  const config = { ...DEFAULT_CONFIG, ...userConfig };

  const wsRef = useRef<WebSocket | null>(null);
  const isInitializedRef = useRef(false);
  const isConnectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [connectionInfo, setConnectionInfo] = useState({
    connectedAt: null as Date | null,
    reconnectAttempts: 0,
    lastError: null as string | null,
  });

  const [performanceMetrics, setPerformanceMetrics] = useState<any>(null);
  const [systemMetrics, setSystemMetrics] = useState<any>(null);
  const [memoryUsage, setMemoryUsage] = useState<any>(null);
  const [networkRequests, setNetworkRequests] = useState<any[]>([]);
  const [networkResponses, setNetworkResponses] = useState<any[]>([]);

  const writeLog = useCallback(
    (message: string, type: TLogType = 'info', data?: any) => {
      if (!config.debugMode) return;

      const style = CONSOLE_STYLES[type];
      const timestamp = new Date().toISOString();

      if (data) {
        console.groupCollapsed(`%c[Debug Time Machine ${timestamp}] ${message}`, style);
        console.log('Data:', data);
        console.groupEnd();
      } else {
        console.log(`%c[Debug Time Machine ${timestamp}] ${message}`, style);
      }
    },
    [config.debugMode]
  );

  const sendMessage = useCallback(
    (type: string, data: any): boolean => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        writeLog(`❌ Cannot send message: WebSocket not ready`, 'warning');
        return false;
      }

      const message = {
        type: type,
        payload: data,
        timestamp: Date.now(),
        clientId: clientId || undefined,
      };

      try {
        const messageStr = JSON.stringify(message);
        wsRef.current.send(messageStr);
        writeLog(`✅ Message sent successfully: ${type}`, 'success');
        return true;
      } catch (error) {
        writeLog(`❌ Failed to send message: ${type}`, 'error');
        return false;
      }
    },
    [clientId, writeLog]
  );

  const captureError = useCallback(
    (error: Error, errorInfo?: any) => {
      writeLog(`🔴 Manual error capture: ${error.message}`, 'error');

      // 🔥 수정: WebSocket 상태를 직접 확인
      const isWsConnected = wsRef.current?.readyState === WebSocket.OPEN;
      writeLog(`Debug: isWsConnected=${isWsConnected}, captureErrors=${config.captureErrors}`, 'info');
      
      if (!isWsConnected || !config.captureErrors) {
        writeLog('Cannot capture error: WebSocket not connected or errors disabled', 'warning');
        return;
      }

      const errorData: IErrorData = {
        message: error.message || 'Unknown error',
        stack: error.stack || 'No stack trace available',
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      if (errorInfo) {
        errorData.errorInfo = errorInfo;
      }

      const success = sendMessage(MessageType.ERROR, errorData);
      writeLog(`Error message send result: ${success}`, success ? 'success' : 'error');
    },
    [config.captureErrors, sendMessage, writeLog]
  );

  const captureStateChange = useCallback(
    (componentName: string, prevState: any, newState: any, props?: any) => {
      const isWsConnected = wsRef.current?.readyState === WebSocket.OPEN;
      
      if (!isWsConnected || !config.captureStateChanges) {
        writeLog('Cannot capture state change: WebSocket not connected or disabled', 'warning');
        return;
      }

      const stateChangeData: IStateChangeData = {
        componentName,
        prevState,
        newState,
        props,
        timestamp: Date.now(),
        url: window.location.href,
      };

      sendMessage(MessageType.STATE_CHANGE, stateChangeData);
    },
    [config.captureStateChanges, sendMessage, writeLog]
  );

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);

        if (!message || typeof message !== 'object' || !message.type) {
          return;
        }

        const messageData = message.payload || message.data;

        switch (message.type) {
          case MessageType.CONNECTION:
            if (messageData && messageData.type === 'welcome' && messageData.clientId) {
              setClientId(messageData.clientId);
              writeLog(`Assigned client ID: ${messageData.clientId}`, 'success');
            }
            break;

          case 'ping':
          case 'PING':
          case MessageType.PING:
            sendMessage(MessageType.PONG, {});
            break;

          case MessageType.ERROR:
            writeLog('Server error:', 'error', messageData);
            break;

          default:
            break;
        }
      } catch (error) {
        writeLog('Failed to parse incoming message', 'error');
      }
    },
    [sendMessage, writeLog]
  );

  const connect = useCallback(() => {
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      writeLog('Connecting to Time Machine...', 'info');
      isConnectingRef.current = true;

      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      wsRef.current = new WebSocket(config.websocketUrl);

      wsRef.current.onopen = () => {
        isConnectingRef.current = false;
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        setConnectionInfo(prev => ({
          ...prev,
          connectedAt: new Date(),
          reconnectAttempts: 0,
          lastError: null,
        }));

        writeLog('Connected to Debug Time Machine!', 'success');

        const connectionData: IConnectionData = {
          type: 'client_ready' as TConnectionType,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
        };

        setTimeout(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            try {
              wsRef.current.send(JSON.stringify({
                type: MessageType.CONNECTION,
                payload: connectionData,
                timestamp: Date.now(),
                clientId: clientId || undefined,
              }));
            } catch (sendError) {
              writeLog('Failed to send initial message', 'error');
            }
          }
        }, 300);
      };

      wsRef.current.onmessage = handleMessage;

      wsRef.current.onclose = event => {
        isConnectingRef.current = false;
        setIsConnected(false);
        setClientId(null);

        if (event.code === 1000 || event.code === 1001) {
          return;
        }

        if (reconnectAttemptsRef.current < config.maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          setConnectionInfo(prev => ({
            ...prev,
            reconnectAttempts: reconnectAttemptsRef.current,
            lastError: null,
          }));

          reconnectTimeoutRef.current = window.setTimeout(() => {
            connect();
          }, config.reconnectInterval);
        }
      };

      wsRef.current.onerror = () => {
        isConnectingRef.current = false;
        setConnectionInfo(prev => ({
          ...prev,
          lastError: 'WebSocket connection error',
        }));
      };
    } catch (error) {
      writeLog('Failed to create WebSocket connection', 'error');
      isConnectingRef.current = false;
    }
  }, [config.websocketUrl, config.maxReconnectAttempts, config.reconnectInterval, handleMessage, writeLog, clientId]);

  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (wsRef.current) {
      wsRef.current.close();
    }

    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  const checkDebugServer = useCallback(async (): Promise<boolean> => {
    if (!config.autoConnect) {
      return false;
    }

    try {
      const baseUrl = config.websocketUrl.replace('ws://', 'http://').replace('/ws', '');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      try {
        const response = await fetch(`${baseUrl}/health`, { 
          method: 'GET',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          writeLog(`🎯 Debug 서버 발견!`, 'success');
          return true;
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        throw fetchError;
      }
    } catch (error) {
      if (config.debugMode) {
        writeLog(`Debug 서버 없음`, 'info');
      }
    }
    return false;
  }, [config.websocketUrl, config.autoConnect, config.debugMode, writeLog]);

  const setupEnhancedNetworkInterceptor = useCallback(() => {
    if (typeof window === 'undefined') return;

    writeLog('🌐 Setting up enhanced network interceptor...', 'info');

    const originalFetch = window.fetch;
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    const generateRequestId = () => `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    const shouldCaptureUrl = (url: string): boolean => {
      const excludePatterns = [
        /localhost:4000/, /localhost:8080/, /127\.0\.0\.1:4000/, /127\.0\.0\.1:8080/,
        /webpack-hmr/, /hot-update/, /sockjs-node/, /@vite\/client/, /react-devtools/,
        /chrome-extension:/, /moz-extension:/, /favicon\.ico/, /manifest\.json/,
        /\.(js|css|png|jpg|jpeg|gif|svg|woff|woff2|ttf|eot)(\?|$)/,
      ];

      if (excludePatterns.some(pattern => pattern.test(url))) {
        return false;
      }

      const includePatterns = [
        /\/api\//, /\/graphql/, /\/v\d+\//, /jsonplaceholder\.typicode\.com/,
        /reqres\.in/, /httpbin\.org/, /api\./,
      ];

      return includePatterns.some(pattern => pattern.test(url));
    };

    const sanitizeRequestData = (data: any): any => {
      if (!data) return data;
      
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          return sanitizeRequestData(parsed);
        } catch {
          return data
            .replace(/"password"\s*:\s*"[^"]*"/gi, '"password":"***"')
            .replace(/"token"\s*:\s*"[^"]*"/gi, '"token":"***"')
            .replace(/"secret"\s*:\s*"[^"]*"/gi, '"secret":"***"');
        }
      }
      
      if (typeof data === 'object' && data !== null) {
        const sanitized = { ...data };
        Object.keys(sanitized).forEach(key => {
          if (/password|token|secret|auth|key/i.test(key)) {
            sanitized[key] = '***';
          }
        });
        return sanitized;
      }
      
      return data;
    };

    window.fetch = async (...args) => {
      const [input, init = {}] = args;
      const url = typeof input === 'string' ? input : (input as Request).url;
      const method = init.method || 'GET';
      const requestId = generateRequestId();
      const startTime = Date.now();
      
      const shouldCapture = shouldCaptureUrl(url) || 
                           ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase());
      
      if (!shouldCapture) {
        return originalFetch.apply(window, args);
      }

      writeLog(`📤 API Request: ${method} ${url}`, 'info');

      const requestData = {
        id: requestId,
        type: 'request',
        method: method.toUpperCase(),
        url,
        timestamp: startTime,
        headers: init.headers ? Object.fromEntries(new Headers(init.headers).entries()) : {},
        body: init.body ? sanitizeRequestData(init.body) : undefined,
      };

      setNetworkRequests(prev => [...prev, {
        ...requestData,
        status: 'pending',
        duration: 0,
      }].slice(-100));

      try {
        const response = await originalFetch.apply(window, args);
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        const responseHeaders = Object.fromEntries(response.headers.entries());
        
        let responseBody = undefined;
        try {
          const clonedResponse = response.clone();
          const contentType = response.headers.get('content-type') || '';
          
          if (contentType.includes('application/json')) {
            responseBody = await clonedResponse.json();
          } else if (contentType.includes('text/')) {
            const text = await clonedResponse.text();
            responseBody = text.length > 1000 ? text.substring(0, 1000) + '...' : text;
          }
        } catch (bodyError) {
          // Ignore body read errors
        }

        const responseData = {
          id: requestId,
          type: 'response',
          method: method.toUpperCase(),
          url,
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          duration,
          timestamp: endTime,
          requestTimestamp: startTime,
          headers: responseHeaders,
          body: responseBody,
          size: responseHeaders['content-length'] || 'unknown',
        };

        setNetworkRequests(prev => prev.map(req => 
          req.id === requestId 
            ? { ...req, ...responseData, status: response.status, success: response.ok }
            : req
        ));

        if (wsRef.current?.readyState === WebSocket.OPEN) {
          writeLog(`🔥 Sending NETWORK_REQUEST to server`, 'info');
          const requestSuccess = sendMessage('NETWORK_REQUEST', requestData);
          const responseSuccess = sendMessage('NETWORK_RESPONSE', responseData);
          writeLog(`Network message results: request=${requestSuccess}, response=${responseSuccess}`, 'info');
        } else {
          writeLog(`❌ WebSocket not ready for network data: readyState=${wsRef.current?.readyState}`, 'warning');
        }

        if (response.ok) {
          writeLog(`✅ API Success: ${method} ${url} (${response.status}, ${duration}ms)`, 'success');
        } else {
          writeLog(`❌ API Error: ${method} ${url} (${response.status}, ${duration}ms)`, 'error');
          
          captureError(new Error(`HTTP ${response.status}: ${response.statusText}`), {
            type: 'network_error',
            url, method, status: response.status, statusText: response.statusText,
            duration, requestId, responseBody,
          });
        }

        return response;
      } catch (error: unknown) {
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        const errorData = {
          id: requestId,
          type: 'error',
          method: method.toUpperCase(),
          url, duration,
          timestamp: endTime,
          requestTimestamp: startTime,
          error: error instanceof Error ? error.message : String(error),
          errorType: error instanceof Error ? error.name : 'Unknown',
        };
        
        setNetworkRequests(prev => prev.map(req => 
          req.id === requestId 
            ? { ...req, ...errorData, status: 'error', success: false }
            : req
        ));

        if (wsRef.current?.readyState === WebSocket.OPEN) {
          writeLog(`🔥 Sending NETWORK_ERROR to server`, 'error');
          const requestSuccess = sendMessage('NETWORK_REQUEST', requestData);
          const errorSuccess = sendMessage('NETWORK_ERROR', errorData);
          writeLog(`Network error message results: request=${requestSuccess}, error=${errorSuccess}`, 'info');
        } else {
          writeLog(`❌ WebSocket not ready for error data: readyState=${wsRef.current?.readyState}`, 'warning');
        }
        
        captureError(error instanceof Error ? error : new Error(String(error)), {
          type: 'network_error',
          url, method, duration, requestId,
        });
        
        throw error;
      }
    };

    XMLHttpRequest.prototype.open = function(method: string, url: string | URL, async?: boolean, username?: string | null, password?: string | null) {
      const urlString = url.toString();
      
      const shouldCapture = shouldCaptureUrl(urlString) || 
                           ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase());
      
      if (shouldCapture) {
        const requestId = generateRequestId();
        
        this._debugTM = {
          requestId,
          method: method.toUpperCase(),
          url: urlString,
          startTime: Date.now(),
          shouldCapture: true,
        };
        
        writeLog(`📤 XHR Request: ${method} ${urlString}`, 'info');
      }
      
      return originalXHROpen.call(this, method, url, async || true, username, password);
    };
    
    XMLHttpRequest.prototype.send = function(body?: XMLHttpRequestBodyInit | Document | null) {
      if (this._debugTM && this._debugTM.shouldCapture) {
        const { requestId, method, url, startTime } = this._debugTM;
        
        const requestData = {
          id: requestId,
          type: 'request',
          method, url,
          timestamp: startTime,
          headers: {},
          body: body ? sanitizeRequestData(body) : undefined,
        };

        setNetworkRequests(prev => [...prev, {
          ...requestData,
          status: 'pending',
          duration: 0,
        }].slice(-100));
        
        this.addEventListener('loadend', () => {
          const endTime = Date.now();
          const duration = endTime - startTime;
          
          let responseBody = undefined;
          try {
            if (this.responseText && this.getResponseHeader('content-type')?.includes('application/json')) {
              responseBody = JSON.parse(this.responseText);
            } else if (this.responseText) {
              const text = this.responseText;
              responseBody = text.length > 1000 ? text.substring(0, 1000) + '...' : text;
            }
          } catch {
            // Ignore parsing errors
          }

          const responseData = {
            id: requestId,
            type: 'response',
            method, url,
            status: this.status,
            statusText: this.statusText,
            ok: this.status >= 200 && this.status < 300,
            duration,
            timestamp: endTime,
            requestTimestamp: startTime,
            headers: {},
            body: responseBody,
          };

          setNetworkRequests(prev => prev.map(req => 
            req.id === requestId 
              ? { ...req, ...responseData, status: this.status, success: this.status >= 200 && this.status < 300 }
              : req
          ));

          if (wsRef.current?.readyState === WebSocket.OPEN) {
            writeLog(`🔥 Sending XHR data to server`, 'info');
            const requestSuccess = sendMessage('NETWORK_REQUEST', requestData);
            
            if (this.status === 0) {
              const errorSuccess = sendMessage('NETWORK_ERROR', {
                id: requestId,
                type: 'error',
                method, url, duration,
                timestamp: endTime,
                requestTimestamp: startTime,
                error: 'Network Error',
                errorType: 'NetworkError',
              });
              writeLog(`XHR error message results: request=${requestSuccess}, error=${errorSuccess}`, 'info');
            } else {
              const responseSuccess = sendMessage('NETWORK_RESPONSE', responseData);
              writeLog(`XHR message results: request=${requestSuccess}, response=${responseSuccess}`, 'info');
            }
          } else {
            writeLog(`❌ WebSocket not ready for XHR data: readyState=${wsRef.current?.readyState}`, 'warning');
          }

          if (this.status >= 200 && this.status < 300) {
            writeLog(`✅ XHR Success: ${method} ${url} (${this.status}, ${duration}ms)`, 'success');
          } else {
            writeLog(`❌ XHR Error: ${method} ${url} (${this.status}, ${duration}ms)`, 'error');
            
            if (this.status >= 400) {
              captureError(new Error(`XHR ${this.status}: ${this.statusText}`), {
                type: 'network_error',
                url, method, status: this.status, statusText: this.statusText,
                duration, requestId, responseBody,
              });
            }
          }
        });

        this.addEventListener('error', () => {
          const endTime = Date.now();
          const duration = endTime - startTime;
          
          const errorData = {
            id: requestId,
            type: 'error',
            method, url, duration,
            timestamp: endTime,
            requestTimestamp: startTime,
            error: 'Network Error',
            errorType: 'NetworkError',
          };

          setNetworkRequests(prev => prev.map(req => 
            req.id === requestId 
              ? { ...req, ...errorData, status: 'error', success: false }
              : req
          ));

          if (wsRef.current?.readyState === WebSocket.OPEN) {
            writeLog(`🔥 Sending XHR error to server`, 'error');
            const requestSuccess = sendMessage('NETWORK_REQUEST', requestData);
            const errorSuccess = sendMessage('NETWORK_ERROR', errorData);
            writeLog(`XHR error message results: request=${requestSuccess}, error=${errorSuccess}`, 'info');
          } else {
            writeLog(`❌ WebSocket not ready for XHR error: readyState=${wsRef.current?.readyState}`, 'warning');
          }
          
          captureError(new Error('XHR Network Error'), {
            type: 'network_error',
            url, method, duration, requestId,
          });
        });
      }
      
      return originalXHRSend.call(this, body);
    };

    writeLog('✅ Enhanced network interceptor setup complete', 'success');
    
    return () => {
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXHROpen;
      XMLHttpRequest.prototype.send = originalXHRSend;
      writeLog('🧹 Enhanced network interceptor cleaned up', 'info');
    };
  }, [writeLog, sendMessage, captureError, isConnected]);

  const autoConnectToServer = useCallback(async (): Promise<(() => void) | null> => {
    if (!config.autoConnect) {
      return null;
    }

    writeLog('🔍 Debug Time Machine 서버 검색 중...', 'info');
    
    const serverExists = await checkDebugServer();
    
    if (serverExists) {
      writeLog('✅ Debug Time Machine 서버 발견! 연결을 시작합니다.', 'success');
      connect();
      return null;
    } else {
      writeLog('🤷 Debug Time Machine 서버를 찾을 수 없습니다. 일반 모드로 실행됩니다.', 'info');
      return null;
    }
  }, [config.autoConnect, checkDebugServer, connect, writeLog]);

  // Initialize connection and event listeners - 🔥 무한루프 수정
  useEffect(() => {
    if (isInitializedRef.current || wsRef.current) {
      return;
    }

    isInitializedRef.current = true;
    writeLog('🚀 Initializing Debug Time Machine client...', 'info');

    const cleanupNetworkInterceptor = setupEnhancedNetworkInterceptor();

    autoConnectToServer().catch((error) => {
      writeLog('자동 연결 중 오류 발생', 'error', error);
    });

    writeLog('✅ Debug Time Machine client initialized!', 'success');

    return () => {
      writeLog('🧹 Cleaning up Debug Time Machine client...', 'info');
      isInitializedRef.current = false;

      if (cleanupNetworkInterceptor) {
        cleanupNetworkInterceptor();
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
  }, []); // 🔥 핵심: 빈 의존성 배열로 한번만 실행

  useEffect(() => {
    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, []);

  const createSnapshot = useCallback((state: Record<string, unknown>, actionType: string) => {
    writeLog(`📸 Snapshot created: ${actionType}`, 'success');
    return { id: Date.now().toString(), state, actionType, timestamp: Date.now() };
  }, [writeLog]);

  const restoreSnapshot = useCallback((snapshotId: string) => {
    writeLog(`⏮️ Snapshot restored: ${snapshotId}`, 'success');
    return true;
  }, [writeLog]);

  const getPerformanceMetrics = useCallback(() => {
    return performanceMetrics;
  }, [performanceMetrics]);

  const getSystemMetrics = useCallback(() => {
    return systemMetrics;
  }, [systemMetrics]);

  const getMemoryUsage = useCallback(() => {
    return memoryUsage;
  }, [memoryUsage]);

  const clearSnapshots = useCallback(() => {
    writeLog('🗑️ All snapshots cleared', 'info');
  }, [writeLog]);

  const exportMetrics = useCallback(() => {
    return { exported: true, timestamp: Date.now() };
  }, []);

  const getNetworkMetrics = useCallback(() => {
    return {
      totalRequests: networkRequests.length,
      successfulRequests: networkRequests.filter((req: any) => req.success === true).length,
      failedRequests: networkRequests.filter((req: any) => req.success === false).length,
      requests: networkRequests,
    };
  }, [networkRequests]);

  const clearNetworkHistory = useCallback(() => {
    setNetworkRequests([]);
    setNetworkResponses([]);
    writeLog('🗑️ Network history cleared', 'info');
  }, [writeLog]);

  const exportNetworkData = useCallback(() => {
    return {
      requests: networkRequests,
      responses: networkResponses,
      metrics: getNetworkMetrics(),
      exportedAt: new Date().toISOString(),
    };
  }, [networkRequests, networkResponses, getNetworkMetrics]);

  return {
    isConnected,
    clientId,
    reconnect,
    sendMessage,
    captureError,
    captureStateChange,
    connectionInfo,
    shouldCaptureNetworkRequest: () => true,
    shouldCaptureNetworkResponse: () => true,
    createSnapshot,
    restoreSnapshot,
    getPerformanceMetrics,
    getSystemMetrics,
    getMemoryUsage,
    clearSnapshots,
    exportMetrics,
    getNetworkMetrics,
    clearNetworkHistory,
    exportNetworkData,
    timeTravelEngine: null,
    metricsCollector: null,
    domSnapshotOptimizer: null,
    networkInterceptor: null,
    performanceMetrics,
    systemMetrics,
    memoryUsage,
    networkRequests,
    networkResponses,
  } as IDebugTimeMachineHookReturn;
}